import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { crmService } from "@/services/crmService";
import { COMMISSION_VIEWS } from "@/config/crmOptions";
import { fmtCents, fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const badge = (s) => ({ paid: "bg-emerald-600 text-white", pending: "bg-amber-500 text-white", estimated: "bg-brand-navy/10 text-brand-navy", cancelled: "bg-secondary text-muted-foreground" }[s] || "bg-secondary");

export default function Commissions() {
  const navigate = useNavigate();
  const [view, setView] = useState("estimated");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { commissions } = await crmService.listCommissions({ view }); setRows(commissions); }
    catch { toast.error("Could not load commissions"); }
    finally { setLoading(false); }
  }, [view]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="commissions-page">
        <PageHeader eyebrow="AgentHQ · Sell" title="Commissions"
          description="Track what you're expecting and what you've been paid. Estimates are projections, not collected income." />

        <div className="flex flex-wrap gap-1 border-b border-border" data-testid="comm-tabs">
          {COMMISSION_VIEWS.map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`comm-tab-${v.key}`}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${view === v.key ? "border-brand-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {v.label}
            </button>
          ))}
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading commissions" /></div>
          : rows.length === 0 ? (
            <EmptyState title="Track what you're expecting and what you've been paid."
              description="Commission records will appear as you add transactions." icon={Wallet} />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-card">
              <Table data-testid="comm-table">
                <TableHeader><TableRow className="bg-secondary/50">
                  <TableHead>Client</TableHead><TableHead>Property</TableHead><TableHead>Transaction Price</TableHead>
                  <TableHead>Gross</TableHead><TableHead>Est. Agent Net</TableHead><TableHead>Status</TableHead>
                  <TableHead>Expected Payment</TableHead><TableHead>Paid</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer" data-testid={`comm-row-${c.id}`} onClick={() => c.transaction?.id && navigate(`/transactions/${c.transaction.id}`)}>
                      <TableCell className="font-medium">{c.contact?.first_name} {c.contact?.last_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{c.transaction?.property_address_line1 || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtCents(c.transaction_price)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtCents(c.gross_commission_amount)}</TableCell>
                      <TableCell className="text-sm font-medium text-foreground">{fmtCents(c.status === "paid" ? c.final_agent_net : c.estimated_agent_net)}</TableCell>
                      <TableCell><Badge className={`text-xs ${badge(c.status)}`}>{c.status}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(c.expected_payment_date)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(c.paid_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </div>
    </AppShell>
  );
}
